import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ShieldCheck, CircleAlert } from 'lucide-react';

const MATCH_STATUS_LABELS = {
  strong_match: { label: 'Strong Match', color: '#4A6741' },
  good_match: { label: 'Good Match', color: '#4A6741' },
  limited_match: { label: 'Limited Match', color: '#D4A55A' },
  no_strong_match: { label: 'No Strong Match', color: '#B45252' },
};

const RANK_LABELS = ['Best Fit', 'Alternative', 'Also Worth Considering'];

function RecommendationBlock({ rec, rankIndex, tenantPath }) {
  return (
    <div className="bg-[#F5F3EF] rounded-xl p-5 border border-[#E8E4DC]" data-testid={`recommendation-card-${rankIndex}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-wider text-[#D4A55A]">
          {RANK_LABELS[rankIndex] || `Option ${rankIndex + 1}`}
        </span>
        {typeof rec.catalog_match_score === 'number' && (
          <span className="text-xs font-semibold text-[#4A6741] bg-[#4A6741]/10 px-2 py-1 rounded-full">
            {Math.round(rec.catalog_match_score)}% catalog match
          </span>
        )}
      </div>
      <h4 className="font-bold text-[#2C3E2D] text-lg">{rec.shaft_brand} {rec.shaft_model}</h4>
      <p className="text-sm text-[#5A6B5C] mb-2">
        {[rec.flex && `Flex: ${rec.flex}`, rec.weight_grams && `Weight: ${rec.weight_grams}g`, rec.price && `${rec.currency || 'USD'} ${rec.price}`]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {rec.alternative_type && rec.alternative_type !== 'primary' && (
        <span className="inline-block text-xs font-medium text-[#4A6741] bg-white border border-[#4A6741]/30 px-2 py-0.5 rounded-full mb-2">
          {rec.alternative_type.replace(/_/g, ' ')}
        </span>
      )}
      <p className="text-sm text-[#2C3E2D] mt-2">{rec.reasoning}</p>
      {rec.tradeoffs && (
        <p className="text-xs text-[#5A6B5C] mt-2 italic">Tradeoff: {rec.tradeoffs}</p>
      )}
      {Array.isArray(rec.missing_product_data) && rec.missing_product_data.length > 0 && (
        <p className="text-xs text-[#5A6B5C]/80 mt-2">
          Based on limited catalog data - missing: {rec.missing_product_data.join(', ')}
        </p>
      )}
      {rec.product_id && (
        <a
          href={tenantPath(`/product/${rec.product_id}`)}
          className="inline-block mt-3 text-sm font-semibold text-[#4A6741] hover:text-[#3A5733] underline"
          data-testid={`view-product-link-${rankIndex}`}
        >
          View This Shaft &rarr;
        </a>
      )}
    </div>
  );
}

export default function FittingResultCard({ recommendationResult, fitProfile, tenantPath }) {
  const [showDetails, setShowDetails] = useState(false);
  if (!recommendationResult) return null;

  const { match_status, fit_confidence, recommendations, customer_summary } = recommendationResult;
  const statusMeta = MATCH_STATUS_LABELS[match_status] || MATCH_STATUS_LABELS.good_match;
  const noMatch = match_status === 'no_strong_match' || !recommendations || recommendations.length === 0;

  const profile = fitProfile?.fit_profile || {};
  const evidence = fitProfile?.evidence_summary || [];
  const limitations = fitProfile?.limitations || [];

  return (
    <div className="mt-4 bg-white rounded-2xl border border-[#E8E4DC] p-6" data-testid="fitting-result-card">
      <div className="flex items-center justify-between mb-3">
        <span
          className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full"
          style={{ color: statusMeta.color, backgroundColor: `${statusMeta.color}1A` }}
          data-testid="match-status-badge"
        >
          {statusMeta.label}
        </span>
        {typeof fit_confidence === 'number' && (
          <div className="flex items-center space-x-2" data-testid="fit-confidence-bar">
            <span className="text-xs text-[#5A6B5C]">Fit Confidence</span>
            <div className="w-24 h-2 bg-[#E8E4DC] rounded-full overflow-hidden">
              <div className="h-full bg-[#4A6741]" style={{ width: `${Math.max(0, Math.min(100, fit_confidence))}%` }} />
            </div>
            <span className="text-xs font-semibold text-[#2C3E2D]">{Math.round(fit_confidence)}%</span>
          </div>
        )}
      </div>

      {customer_summary?.headline && (
        <h3 className="text-xl font-bold text-[#2C3E2D] mb-1">{customer_summary.headline}</h3>
      )}
      {customer_summary?.why_it_fits && (
        <p className="text-sm text-[#5A6B5C] mb-4">{customer_summary.why_it_fits}</p>
      )}

      {noMatch ? (
        <div className="flex items-start space-x-3 bg-[#B45252]/10 border border-[#B45252]/30 rounded-xl p-4" data-testid="no-match-message">
          <CircleAlert className="h-5 w-5 text-[#B45252] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#2C3E2D]">
            {customer_summary?.no_match_message || "Nothing in the current catalog is a strong match for this fit profile."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {recommendations.map((rec, i) => (
            <RecommendationBlock key={rec.product_id || i} rec={rec} rankIndex={i} tenantPath={tenantPath} />
          ))}
          {customer_summary?.alternative_summary && (
            <p className="text-xs text-[#5A6B5C] italic px-1">{customer_summary.alternative_summary}</p>
          )}
        </div>
      )}

      {Object.keys(profile).length > 0 && (
        <div className="mt-5 border-t border-[#E8E4DC] pt-4">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center space-x-2 text-sm font-semibold text-[#4A6741] hover:text-[#3A5733]"
            data-testid="toggle-fitting-details"
          >
            <ShieldCheck className="h-4 w-4" />
            <span>View fitting details</span>
            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showDetails && (
            <div className="mt-3 space-y-3 text-sm" data-testid="fitting-details-panel">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(profile).map(([key, value]) => (
                  value !== null && value !== undefined && value !== '' && (
                    <div key={key} className="bg-[#F5F3EF] rounded-lg px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-[#5A6B5C]">{key.replace(/_/g, ' ')}</p>
                      <p className="text-[#2C3E2D] font-medium">{String(value)}</p>
                    </div>
                  )
                ))}
              </div>
              {evidence.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-[#2C3E2D] mb-1">Evidence used</p>
                  <ul className="text-xs text-[#5A6B5C] list-disc list-inside space-y-0.5">
                    {evidence.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              {limitations.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-[#2C3E2D] mb-1">Limitations</p>
                  <ul className="text-xs text-[#5A6B5C] list-disc list-inside space-y-0.5">
                    {limitations.map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                </div>
              )}
              {fitProfile?.data_quality && (
                <p className="text-xs text-[#5A6B5C]">Data quality: <span className="font-semibold text-[#2C3E2D]">{fitProfile.data_quality}</span></p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
